import { createFileRoute, Link, Outlet, useLocation, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { BarChart3, Boxes, Truck, ArrowDownToLine, Receipt, Activity, Wrench } from "lucide-react";
import type { ReactNode } from "react";
import { dateRangeSearchSchema, retainDateRangeKeys } from "@/lib/date-range-search";
import { DateRangeFilter, type DateRangeValue } from "@/components/filters/date-range-filter";

export const Route = createFileRoute("/_authenticated/app/reports")({
  component: ReportsLayout,
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
});

const TABS: { to: string; label: string; icon: ReactNode }[] = [
  { to: "/app/reports", label: "Executive", icon: <BarChart3 className="h-4 w-4" /> },
  { to: "/app/reports/warehouse", label: "Warehouse Ops", icon: <Activity className="h-4 w-4" /> },
  { to: "/app/reports/inventory", label: "Inventory", icon: <Boxes className="h-4 w-4" /> },
  { to: "/app/reports/shipping", label: "Shipping", icon: <Truck className="h-4 w-4" /> },
  { to: "/app/reports/receiving", label: "Receiving", icon: <ArrowDownToLine className="h-4 w-4" /> },
  { to: "/app/reports/billing", label: "Billing", icon: <Receipt className="h-4 w-4" /> },
  { to: "/app/reports/builder", label: "Builder", icon: <Wrench className="h-4 w-4" /> },
];

function ReportsLayout() {
  const loc = useLocation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const value: DateRangeValue = { start: search.start, end: search.end };
  const setValue = (v: DateRangeValue) => {
    navigate({ search: (prev: any) => ({ ...prev, start: v.start, end: v.end }), replace: true });
  };
  return (
    <div className="flex flex-col min-h-screen">
      <nav className="border-b bg-card overflow-x-auto sticky top-0 z-10">
        <div className="flex items-center gap-1 px-3 sm:px-6">
          {TABS.map((t) => {
            const active = loc.pathname === t.to || (t.to !== "/app/reports" && loc.pathname.startsWith(t.to));
            return (
              <Link
                key={t.to}
                to={t.to}
                className={`flex items-center gap-2 px-3 py-3 text-sm border-b-2 whitespace-nowrap transition-colors ${
                  active
                    ? "border-primary text-foreground font-medium"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.icon} {t.label}
              </Link>
            );
          })}
          <div className="ml-auto py-2 pl-3">
            <DateRangeFilter value={value} onChange={setValue} align="end" />
          </div>
        </div>
      </nav>
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}